<!--
 * @Description: 个体开票内容
 * @Date: 2025-09-16 13:50:09
-->

<template>
  <BaseForm
    v-if="modelValue"
    ref="formRef"
    label-position="right"
    scrollToError
    :model="modelValue"
    :rules="formRules"
  >
    <!-- <BaseFormItem label="结算月度" prop="taskAt">
      <BaseDatePicker
        class="form-dialog__w280"
        style="width: 280px"
        type="month"
        validate-event
        :disabled="disabled"
        v-model="modelValue.taskAt"
      ></BaseDatePicker>
    </BaseFormItem> -->
    <BaseFormItem label="发票照片" prop="imgList">
      <div>
        <BaseUpload
          v-if="!disabled"
          style="margin-bottom: 20px"
          autoUpload
          btnText="上传"
          tipText="只能上传jpg/png，且每份不超过50MB"
          :listType="null"
          :accept="['.jpg', '.jpeg', '.png', '.JPG', '.JPEG', '.PNG']"
          :limit="20"
          :limitSize="50"
          :multiple="false"
          :showFileList="false"
          :http-request="uploadApi"
          @change="onImgChange"
        ></BaseUpload>
        <div
          style="display: flex; align-items: flex-end; margin-bottom: 30px"
          v-for="(img, imgIndex) in modelValue.imgList"
          :key="img.invoicePic"
        >
          <!-- 居左 -->
          <BaseImage
            style="width: 200px; height: 120px"
            :src="img.invoicePic"
            :preview-src-list="[img.invoicePic]"
          />
          <!-- 居右 -->
          <div
            style="
              margin-left: 20px;
              display: flex;
              align-items: flex-end;
              flex-wrap: wrap;
            "
          >
            <!-- 居上 -->
            <div v-if="isType7" style="width: 100%">
              <span style="margin-right: 3px; display: block">开票日期</span>
              <BaseDatePicker
                style="width: 240px"
                type="date"
                validate-event
                :disabled="disabled"
                v-model="img.invoiceDate"
              />
            </div>
            <!-- 居下 -->
            <div
              style="
                width: 100%;
                display: flex;
                align-items: flex-end;
                flex-wrap: wrap;
              "
            >
              <div style="margin-right: 15px">
                <span style="display: block">票面金额</span>
                <BaseInputNumber
                  style="width: 240px; display: block"
                  placeholder="请输入"
                  :disabled="disabled"
                  :controls="false"
                  :precision="2"
                  :min="0"
                  :max="999999999"
                  v-model="img.invoiceAmount"
                />
              </div>
              <div style="position: relative">
                <span>发票号码</span>
                <BaseInput
                  style="width: 240px"
                  :disabled="disabled"
                  v-model="img.invoiceNo"
                />
                <BaseIcon
                  v-if="!disabled"
                  size="18"
                  color="#ED7067"
                  style="
                    cursor: pointer;
                    position: absolute;
                    right: -35px;
                    bottom: 10px;
                  "
                  @click="onImgRemove(imgIndex)"
                >
                  <DeleteFilled />
                </BaseIcon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseFormItem>
    <BaseFormItem
      label="其他材料"
      prop="fileList"
      v-if="!disabled || modelValue.fileList?.length"
    >
      <BaseUpload
        style="margin-bottom: 20px"
        listType="text"
        autoUpload
        showFileList
        btnText="添加文件"
        :tipText="
          disabled ? '' : '只能上传png/jpg/jpeg/pdf/bmp文件，且每份不超过50MB'
        "
        :accept="['.jpg', '.jpeg', '.png', '.pdf', '.bmp']"
        :limit="disabled ? 1 : 10"
        :limitSize="50"
        :multiple="false"
        :http-request="uploadApi"
        :disabled="disabled"
        :uploadBtnProps="{
          limitOverHide: true
        }"
        v-model:fileList="modelValue.fileList"
        @preview="onFilePreview"
      ></BaseUpload>
    </BaseFormItem>
  </BaseForm>
</template>

<script lang="tsx" setup>
import { ref, computed } from 'vue';
import {
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseImage,
  BaseUpload,
  BaseIcon,
  BaseInputNumber,
  BaseDatePicker
} from 'xrk-components';
import { uploadApi } from '@data-source/common/apis';
import { MakeOutAnInvoiceParams } from '@V/invoice/src/data-source/types';
import { DeleteFilled } from '@element-plus/icons-vue';
import { useRecognition } from '@V/invoice/src/hooks';
import { StateType } from '@cTypes/order';

const props = withDefaults(
  defineProps<{
    id?: number; // 发票申请详情id
    invoiceBusinessType?: StateType;
  }>(),
  { invoiceBusinessType: 7 }
);

const modelValue = defineModel<MakeOutAnInvoiceParams>(); // 集合中只会有一个元素
const formRef = ref(null);
const disabled = ref(!!props.id);

const formRules = {
  // taskAt: [
  //   {
  //     required: true,
  //     trigger: 'change',
  //     message: '请选择结算月度'
  //   }
  // ],
  imgList: [
    {
      required: true,
      trigger: 'change',
      validator: (rule, v, callback) => {
        if (!v?.length) {
          return callback(new Error('请上传发票'));
        }
        if (v.some(item => !item.invoicePic)) {
          return callback(new Error('请重新上传发票'));
        }
        if (v.some(item => !item.invoiceAmount || !item.invoiceNo)) {
          return callback(new Error('请填写发票信息'));
        }
        if (v.some(item => !item.invoiceDate && isType7.value)) {
          return callback(new Error('请填写开票日期'));
        }
        callback();
      }
    }
  ],
  fileList: [
    {
      required: false,
      trigger: 'change',
      validator: (rule, v, callback) => {
        if (v.some(item => !item.url)) {
          return callback(new Error('请重新上传资料'));
        }
        callback();
      }
    }
  ]
};

const isType7 = computed(() => true); // 写死个体7

const onImgChange = async v => {
  const { response } = v;
  if (response && response.data.fileUrl) {
    const ocr = await useRecognition(response.data.fileUrl);
    modelValue.value.imgList.push({
      id: null,
      invoicePic: response.data.fileUrl,
      invoiceType: 1, // 写死，只有蓝票
      invoiceApplyId: props.id,
      remark: null,
      createAt: Date.now(),
      ...ocr
    });
  }
  formRef.value.validate('imgList');
};
const onImgRemove = index => {
  modelValue.value.imgList.splice(index, 1);
  formRef.value.validate('imgList');
};
const onFilePreview = v => {
  window.open(v.url);
};

defineExpose({
  validate: (...args: any) => formRef.value?.validate(...args)
});
</script>
